import { PaymentProvider, PaymentProviderName } from '@monorepo/types';

/**
 * Payment provider i18n keys mapping
 * These keys should be translated in common.json under paymentProviders namespace
 */
/** Mock provider is backend-only and intentionally omitted. */
const paymentProviderDisplayName: Partial<Record<PaymentProviderName, string>> =
  {
    [PaymentProviderName.PaySafe]: 'common:paymentProviders.paysafe',
    [PaymentProviderName.Free]: 'common:paymentProviders.free',
    [PaymentProviderName.PayPal]: 'common:paymentProviders.paypal',
    [PaymentProviderName.Checkout]: 'common:paymentProviders.checkout',
    [PaymentProviderName.Confirmo]: 'common:paymentProviders.confirmo',
    [PaymentProviderName.Fibonatix]: 'common:paymentProviders.fibonatix',
    [PaymentProviderName.UpiIndia]: 'common:paymentProviders.upiIndia',
    [PaymentProviderName.HubCredits]: 'common:paymentProviders.hubCredits',
    [PaymentProviderName.Paytiko]: 'common:paymentProviders.paytiko',
  };

/**
 * Get display name for a payment provider.
 * Uses displayName from DB if available, otherwise falls back to translated paymentProviderDisplayName.
 *
 * @param provider - PaymentProvider object or PaymentProviderName string (can be undefined or null)
 * @param t - Optional translation function from react-i18next. If provided, translates the i18n key.
 * @returns Translated display name, DB displayName, or 'N/A' if provider is undefined/null
 *
 * @example
 * // With translation function
 * const { t } = useTranslation(['common']);
 * const name = getPaymentProviderDisplayName(provider, t);
 *
 * @example
 * // Without translation (returns i18n key)
 * const key = getPaymentProviderDisplayName(provider);
 */
export function getPaymentProviderDisplayName(
  provider: PaymentProvider | PaymentProviderName | undefined | null,
  t?: (key: string) => string,
): string {
  // Handle undefined or null
  if (!provider) {
    return t ? t('common:not_available') : 'N/A';
  }

  // If it's just a string (PaymentProviderName), use fallback const
  if (typeof provider === 'string') {
    const i18nKey = paymentProviderDisplayName[provider] || provider;
    return t ? t(i18nKey) : i18nKey;
  }

  // If provider has a custom displayName from DB, use it directly (no translation needed)
  if (provider.displayName) {
    return provider.displayName;
  }

  // Otherwise use the i18n key mapping
  const i18nKey =
    paymentProviderDisplayName[provider.name] ||
    provider.name ||
    (t ? t('common:not_available') : 'N/A');

  return t ? t(i18nKey) : i18nKey;
}
